import { CavernsSubmodeKeyType } from 'dt-common/generated/types/enums';
import { CavernsData, GameItem } from 'dt-common/generated/zod';
import { EventEmitter } from 'events';
import {
  CavernsDispatcher,
  PlayerDispatcher,
  SettingsDispatcher,
  UIDispatcher,
} from '~/flux/dispatchers';
import { logError } from '~/logger';
import text from '~/text';
import { awaitSocket, registerDispatchHandlers } from '~/Tools';
import * as handlers from './handlers';
import messages from './messages';
import { InitNewCavernsMapResult } from 'dt-common/generated/graphql-types';
import { Socket } from 'socket.io-client';

let playerId: string | null = null;
let _socket: Socket;
let _worker: Worker;
let _current_game_submode: CavernsSubmodeKeyType;
let _last_caverns_travel_at = 0;
let _execute_caverns_travel_timeout: NodeJS.Timeout;

// the stuff we serve:
let caverns_data: CavernsData;
let pending_caverns_travel = false;
let current_caverns_level: number;
let battleState;
let goldPickedUp: number;
let pdPickedUp: number;
let itemsPickedUp: GameItem[] = [];
let actingAIUnit: string;

const CavernsStore = Object.assign({}, EventEmitter.prototype, {
  ...messages,

  getAll() {
    return {
      caverns_data,
      pending_caverns_travel,
      current_caverns_level,
      battleState,
      goldPickedUp,
      pdPickedUp,
      itemsPickedUp,
      actingAIUnit,
    };
  },
});
// Initialize EventEmitter internal state
EventEmitter.call(CavernsStore);
export default CavernsStore;

const emit = CavernsStore.emit.bind(CavernsStore);

CavernsStore.on(messages.RETURNED_TO_SURFACE, (data) => {
  battleEngineCleanup();
  caverns_data[data.game_submode].current_run = null;
});

PlayerDispatcher.register(
  registerDispatchHandlers({
    [PlayerDispatcher.PLAYER_LOGGED_IN]: (action: { player: any; }) => {
      return handlers.onPlayerLoggedIn({ setPlayerId, setCavernsData, emit }, action);
    },
  })
);
CavernsDispatcher.register(
  registerDispatchHandlers({
    [CavernsDispatcher.CLAIM_PASSIVE_YIELDS]: claimPassiveYields,
    [CavernsDispatcher.INIT_NEW_CAVERNS_MAP]: (action: {
      startingLevel: number;
      game_submode: string;
    }) => {
      return handlers.initNewCavernsMap(
        {
          playerId,
          setPendingCavernsTravel,
          setCurrentGameSubmode,
          emit,
        },
        action
      );
    },
    [CavernsDispatcher.LOOT_TILE]: async (action: { tileId: string; }) => {
      await handlers.lootCavernsTile(
        { playerId, emit, setLootPickedUp, _worker, onMissingCavernsRunData },
        {
          game_submode: _current_game_submode, tile_id: action.tileId
        });
    },
    [CavernsDispatcher.DOOR_SPRITE_CLICK]: onDoorSpriteClick,
    [CavernsDispatcher.RETURN_TO_SURFACE]: async () => {
      await handlers.returnToSurface(
        {
          playerId: playerId!,
          emit,
          onMissingCavernsRunData,
        },
        { game_submode: _current_game_submode }
      );
    },
    [CavernsDispatcher.SET_PORTAL_LEVEL_TO_CURRENT_DEPTH]: async (action: { game_submode: string; }) => {
      await handlers.setPortalLevelToCurrentDepth(
        {
          playerId,
          setCavernsData,
          emit,
          onMissingCavernsRunData,
        },
        { game_submode: action.game_submode }
      );
    },
    [CavernsDispatcher.START_CAVERNS_BATTLE_ENGINE]: (action: { game_submode: CavernsSubmodeKeyType, new_caverns_data: InitNewCavernsMapResult, white_team; }) => {
      try {
        const { game_submode } = action;
        if (game_submode !== _current_game_submode)
          throw new Error(`game_submode mismatch: ${{ game_submode, _current_game_submode }}`);

        setPendingCavernsTravel(false);
        battleEngineCleanup();

        const { current_run } = caverns_data[game_submode];
        if (current_run) {
          setLootPickedUp({ gold: current_run.gold_picked_up ?? 0, pd: current_run.pd_picked_up ?? 0, items: current_run.items_picked_up ?? [] });
        }

        return handlers.startCavernsBattleEngine(
          {
            playerId,
            setWorker,
            setActingAIUnit,
            getBattleState,
            setBattleState,
            onDoorSpriteClick,
            getCurrentCavernsLevel,
            setCurrentCavernsLevel,
            emit,
            onMissingCavernsRunData,
          },
          action
        );
      } catch (err) {
        logError(err, {
          module: 'CavernsStore',
          func: 'startCavernsBattleEngine',
          playerId,
          action: JSON.stringify(action),
        });
        handlers.returnToSurface({ playerId: playerId!, emit, onMissingCavernsRunData }, { game_submode: _current_game_submode });
      }
    },
    [CavernsDispatcher.DISCARD_LOOT_ITEM]: async (action: { item_id: string; }) => {
      await handlers.discardCavernsLootItem(
        {
          playerId,
          setLootPickedUp,
          getCurrentLoot,
          emit,
          onMissingCavernsRunData,
        },
        { game_submode: _current_game_submode, item_id: action.item_id }
      );
    },
  })
);
UIDispatcher.register(
  registerDispatchHandlers({
    [UIDispatcher.SELECT_GAME_SUBMODE]: onGameSubmodeSelection,
  })
);
SettingsDispatcher.register(
  registerDispatchHandlers({
    [SettingsDispatcher.CHANGE_BROWSER_LANGUAGE]: onBrowserLanguageChanged,
  })
);

awaitSocket(onSocketConnected);
function onSocketConnected(socket) {
  try {
    _socket = socket;

    if (!_socket.has_CavernsStore_listeners) {
      socket.on('api_server_error', onApiServerError);
      _socket.on('caverns_passive_yield_claimed', onPassiveCavernsYieldsClaimed);
      _socket.on('missing_caverns_run_data', onMissingCavernsRunData);
      _socket.has_CavernsStore_listeners = true;
    }
  } catch (err) {
    logError(err, {
      module: 'CavernsStore',
      func: 'onSocketConnected',
    });
  }
}

function setPlayerId(val: string) {
  playerId = val;
}

function setCavernsData(val: CavernsData) {
  caverns_data = val;
}

function setPendingCavernsTravel(val: boolean) {
  pending_caverns_travel = val;
}

function getCurrentCavernsLevel() {
  return current_caverns_level;
}

function setCurrentCavernsLevel(val: number) {
  current_caverns_level = val;
}

function setCurrentGameSubmode(val: string) {
  _current_game_submode = val;
}

function setWorker(val: Worker) {
  _worker = val;
}

function setActingAIUnit(val: string) {
  actingAIUnit = val;
}

function getBattleState() {
  return battleState;
}

function setBattleState(val) {
  battleState = val;
}

function setLootPickedUp({ gold, pd, items }: { gold: number, pd: number, items: GameItem[]; }) {
  goldPickedUp = gold;
  pdPickedUp = pd;
  itemsPickedUp = items;
}

function getCurrentLoot() {
  return {
    gold: goldPickedUp,
    pd: pdPickedUp,
    items: itemsPickedUp,
  };
}

function claimPassiveYields(action) {
  _socket.emit('claim_passive_caverns_yields', {
    playerId,
    game_submode: action.game_submode,
  });
}

function battleEngineCleanup() {
  _worker?.postMessage({ message_name: 'cleanup' });

  goldPickedUp = 0;
  pdPickedUp = 0;
  itemsPickedUp = [];
  battleState = null;
}

function proxyToWorker(message_name, message_data) {
  _worker.postMessage({
    message_name,
    message_data,
  });
}

function onPassiveCavernsYieldsClaimed(data) {
  caverns_data = data.caverns_data;
  CavernsStore.emit(messages.PASSIVE_YIELDS_CLAIMED, data);
}

function onDoorSpriteClick(action) {
  clearTimeout(_execute_caverns_travel_timeout);

  // throttle caverns travel 1 per 6 seconds
  const travel_delta = Date.now() - _last_caverns_travel_at;
  _execute_caverns_travel_timeout = setTimeout(
    executeCavernsTravel.bind(null, action),
    travel_delta < 6000 ? 6000 - travel_delta : 0
  );
}

function executeCavernsTravel(action) {
  try {
    if (pending_caverns_travel) {
      return;
    }

    const all_units_array = Object.values(battleState.allUnits || {});

    // the player can't change rooms if there are any living mobs in the current room
    for (const { team, dead, inPlay } of all_units_array) {
      if (team === 'black' && !dead && inPlay) {
        $addMessageLogMessage(text('ui.enemies_in_play'), 0xff0000);
        return;
      }
    }

    pending_caverns_travel = true;
    // handle case where we just don't get an API response
    setTimeout(() => {
      pending_caverns_travel = false;
    }, 4000);

    _worker.postMessage({ message_name: 'pause' });

    void handlers.cavernsTravel({
      playerId,
      game_submode: action.game_submode,
      direction: action.direction,
      survivingWhiteUnits: all_units_array.filter(
        ({ team, dead, inPlay }) => team === 'white' && !dead && inPlay
      ),
      setPendingCavernsTravel,
      setCavernsData,
      setLootPickedUp,
      emit,
      proxyToWorker,
      onMissingCavernsRunData,
    });

    CavernsStore.emit(messages.CAVERNS_TRAVEL_STARTED);

    _last_caverns_travel_at = Date.now();
  } catch (err) {
    logError(err, {
      module: 'CavernsStore',
      func: 'onDoorSpriteClick',
      action,
    });
  }
}

function onMissingCavernsRunData() {
  battleEngineCleanup();
  CavernsStore.emit(messages.MISSING_CAVERNS_RUN_DATA);
}


function onApiServerError() {
  pending_caverns_travel = false;
}

function onBrowserLanguageChanged() {
  handlers.returnToSurface({ playerId: playerId!, emit, onMissingCavernsRunData }, { game_submode: _current_game_submode });
}


function onGameSubmodeSelection(action) {
  setCurrentGameSubmode(action.gameSubmodeKey);
}
